import type { Lang } from "../index";

export interface ZoomAutoMeetingNotesCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: {
    heading: string;
    body: string;
    bullets: string[];
  };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: ZoomAutoMeetingNotesCopy = {
  seo: {
    title: "Auto meeting notes for Zoom — and any other call platform",
    description:
      "Auto meeting notes for Zoom, Teams, and Meet. PMs and EMs running 4–8 calls a day get TL;DR + action items in seconds — no bot in the call, no meeting hijacked.",
  },
  eyebrow: "Use case · Auto meeting notes",
  h1: "Stop paying the meeting notes tax.",
  lede:
    "PMs and EMs run 4-8 meetings a day. Either you skip the notes and forget the action items, or you spend 15 minutes after each call typing them up. Either way the day is gone. Minuta auto-generates the notes — on Zoom, Teams, Meet, anywhere.",
  problem: {
    heading: "The meeting notes tax",
    body1:
      "You finish the customer call. Three commitments were made — one of them yours, two for the SE. You promise yourself you'll write the followup as soon as the next meeting ends. The next meeting runs 7 minutes long. The followup never gets written. Friday afternoon, the customer pings: \"any update on what we discussed?\" — and you genuinely don't remember the third commitment.",
    body2:
      "The other failure mode: you write the notes diligently, but it costs you 15 minutes per meeting. Six meetings a day = 90 minutes of pure note-typing. That's not a meeting notes problem; that's a \"your day is gone to clerical work\" problem.",
  },
  scenarios: {
    heading: "Where it pays off",
    items: [
      {
        tag: "Customer call",
        title: "Followup email at zero effort",
        body: "Call ends. Minuta auto-generates a TL;DR + key points + action items grounded in the actual transcript. You glance at it, copy-paste into the followup email, edit one line. 30 seconds vs. 15 minutes.",
      },
      {
        tag: "Sprint planning",
        title: "Action items into your tracker",
        body: "Sprint planning runs 60 minutes. Five committed work items. Minuta extracts them as a structured action-item list — owner, deliverable, deadline if mentioned. You drop them into Linear/Jira without re-reading anything.",
      },
      {
        tag: "1-on-1",
        title: "Notes for your private record",
        body: "1-on-1 ends. You usually write a 4-line summary into your manager doc. Minuta has it ready: what they said, what you committed, action items for next session. Paste, done.",
      },
    ],
  },
  how: {
    heading: "How Minuta plugs into your meetings",
    body:
      "Open Minuta before the call starts. Hit record. Run the meeting as you always would — Minuta captures system audio in the background and turns it into structured notes the moment you stop.",
    bullets: [
      "Works on Zoom, Teams, Meet, FaceTime, and any other call that plays through your computer's system audio. No platform-specific plugin or admin install.",
      "Live transcript runs during the call (you can ignore it and just talk).",
      "Call ends → structured summary appears: TL;DR, key points, action items. Grounded in the actual transcript, not hallucinated.",
      "Export as plain text, markdown, or copy-paste straight into your tool of choice. No CRM lock-in.",
    ],
  },
  proof: {
    heading: "Why pick Minuta over Zoom AI Companion or notetaker bots",
    bullets: [
      "Zoom AI Companion only works in Zoom. Minuta works in Zoom, Teams, Meet, FaceTime, native apps, browser calls — anything.",
      "Otter, Fireflies, tl;dv require a bot to join your call. Minuta runs on your computer and captures system audio invisibly. No \"Otter has joined the meeting\" awkwardness.",
      "Saved in your desktop app. Transcripts, TL;DR, and action items live in the local SQLite database — review, export to Markdown, or delete anytime. Especially useful for sensitive customer or 1-on-1 calls.",
      "Free tier covers 1h/month — enough to test on real calls before committing.",
    ],
  },
  cta: {
    heading: "Stop writing meeting notes",
    body: "7 days with everything unlocked. 1h/month on Free after. €9/mo for unlimited Pro.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: ZoomAutoMeetingNotesCopy = {
  seo: {
    title: "Ata automática Zoom — e qualquer outra call",
    description:
      "Ata automática pra Zoom, Teams e Meet. PM e EM com 4–8 calls por dia recebem TL;DR + action items em segundos — sem bot na call, sem reunião sequestrada.",
  },
  eyebrow: "Caso de uso · Ata automática",
  h1: "Para de pagar o imposto da ata de reunião.",
  lede:
    "PM e EM rodam 4-8 calls por dia. Ou você pula a ata e esquece os action items, ou gasta 15 minutos depois de cada call digitando tudo. De qualquer jeito o dia foi embora. O Minuta gera a ata automática — no Zoom, Teams, Meet, em qualquer lugar.",
  problem: {
    heading: "O imposto da ata de reunião",
    body1:
      "Você termina a call com o cliente. Foram três compromissos — um seu, dois do SE. Você promete pra si mesmo que vai mandar o followup assim que a próxima call acabar. A próxima call estoura 7 minutos. O followup nunca é escrito. Sexta de tarde o cliente cutuca: \"alguma atualização sobre o que falamos?\" — e você genuinamente não lembra do terceiro compromisso.",
    body2:
      "A outra falha: você escreve a ata religiosamente, mas custa 15 minutos por reunião. Seis reuniões por dia = 90 minutos só digitando ata. Não é problema de ata; é o seu dia inteiro indo embora em trabalho burocrático.",
  },
  scenarios: {
    heading: "Onde paga o investimento",
    items: [
      {
        tag: "Call com cliente",
        title: "Followup email a custo zero",
        body: "Call acaba. O Minuta gera a ata automática com TL;DR + pontos-chave + action items, fundamentada no transcript real. Você dá uma olhada, cola no email de followup, ajusta uma linha. 30 segundos contra 15 minutos.",
      },
      {
        tag: "Sprint planning",
        title: "Action items direto pro tracker",
        body: "Sprint planning roda 60 minutos. Cinco entregas combinadas. O Minuta extrai como lista estruturada de action items — responsável, entrega, deadline quando mencionado. Você joga no Linear/Jira sem reler nada.",
      },
      {
        tag: "1-on-1",
        title: "Notas pro seu registro privado",
        body: "1-on-1 acaba. Você normalmente escreve 4 linhas de resumo no seu doc de gestor. O Minuta já tem pronto: o que ele disse, o que você se comprometeu, action items pra próxima sessão. Cola, fim.",
      },
    ],
  },
  how: {
    heading: "Como o Minuta entra nas suas reuniões",
    body:
      "Abre o Minuta antes da call começar. Aperta gravar. Roda a reunião como sempre roda — o Minuta captura o áudio do sistema em background e transforma em ata estruturada no momento em que você para.",
    bullets: [
      "Funciona em Zoom, Teams, Meet, FaceTime e qualquer outra call que toque pelo áudio do sistema do computador. Sem plugin específico de plataforma nem instalação por admin.",
      "Transcript ao vivo rodando durante a call (dá pra ignorar e só falar).",
      "Call acaba → ata estruturada aparece: TL;DR, pontos-chave, action items. Fundamentada no transcript real, sem alucinação.",
      "Exporta como texto puro, markdown, ou cola direto na ferramenta da sua escolha. Sem lock-in de CRM.",
    ],
  },
  proof: {
    heading: "Por que escolher Minuta em vez do Zoom AI Companion ou bot de notetaker",
    bullets: [
      "Zoom AI Companion só funciona no Zoom. O Minuta funciona em Zoom, Teams, Meet, FaceTime, apps nativos, calls de browser — qualquer coisa.",
      "Otter, Fireflies, tl;dv precisam mandar um bot pra entrar na call. O Minuta roda no seu computador e captura o áudio do sistema invisivelmente. Sem aquele constrangimento do \"Otter entrou na reunião\".",
      "Salvo no seu app desktop. Transcript, TL;DR e action items ficam no banco SQLite local — revise, exporte para Markdown ou delete quando quiser. Especialmente útil para calls sensíveis com cliente ou 1-on-1.",
      "Free tier cobre 1h/mês — suficiente pra testar em calls reais antes de assinar.",
    ],
  },
  cta: {
    heading: "Para de escrever ata",
    body: "7 dias com tudo desbloqueado. Depois, 1h/mês no Free. €9/mês pra Pro ilimitado.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: ZoomAutoMeetingNotesCopy = {
  seo: {
    title: "Acta automática reunión IA — Zoom y cualquier plataforma",
    description:
      "Acta automática para Zoom, Teams y Meet con IA. PMs y EMs con 4–8 calls al día reciben TL;DR + action items en segundos — sin bot en la call, sin reunión secuestrada.",
  },
  eyebrow: "Caso de uso · Acta automática",
  h1: "Deja de pagar el impuesto del acta de reunión.",
  lede:
    "PMs y EMs corren 4-8 reuniones al día. O te saltas el acta y olvidas los action items, o gastas 15 minutos después de cada call escribiéndola. De cualquier forma el día se fue. Minuta genera el acta automática de la reunión con IA — en Zoom, Teams, Meet, donde sea.",
  problem: {
    heading: "El impuesto del acta de reunión",
    body1:
      "Terminas la call con el cliente. Fueron tres compromisos — uno tuyo, dos del SE. Te prometes que vas a mandar el followup en cuanto termine la próxima call. La próxima call se pasa 7 minutos. El followup nunca se escribe. Viernes por la tarde el cliente te pinga: \"¿alguna novedad sobre lo que hablamos?\" — y genuinamente no te acuerdas del tercer compromiso.",
    body2:
      "El otro modo de fallo: escribes el acta religiosamente, pero te cuesta 15 minutos por reunión. Seis reuniones al día = 90 minutos puramente tecleando acta. Eso no es un problema de acta; es \"tu día se fue en trabajo burocrático\".",
  },
  scenarios: {
    heading: "Dónde paga la inversión",
    items: [
      {
        tag: "Call con cliente",
        title: "Followup email a coste cero",
        body: "Termina la call. Minuta genera el acta automática con TL;DR + puntos clave + action items, basada en el transcript real. La revisas un segundo, la pegas en el email de followup, ajustas una línea. 30 segundos vs. 15 minutos.",
      },
      {
        tag: "Sprint planning",
        title: "Action items directo a tu tracker",
        body: "Sprint planning dura 60 minutos. Cinco entregas comprometidas. Minuta las extrae como lista estructurada de action items — responsable, entregable, deadline si se mencionó. Las pegas en Linear/Jira sin releer nada.",
      },
      {
        tag: "1-on-1",
        title: "Notas para tu registro privado",
        body: "Termina el 1-on-1. Normalmente escribes un resumen de 4 líneas en tu doc de manager. Minuta ya lo tiene listo: lo que dijo, lo que te comprometiste, action items para la próxima sesión. Pegar, listo.",
      },
    ],
  },
  how: {
    heading: "Cómo entra Minuta en tus reuniones",
    body:
      "Abre Minuta antes de que empiece la call. Pulsa grabar. Corre la reunión como siempre — Minuta captura el audio del sistema en segundo plano y lo convierte en acta estructurada en el momento en que paras.",
    bullets: [
      "Funciona en Zoom, Teams, Meet, FaceTime y cualquier otra call que suene por el audio del sistema de tu computadora. Sin plugin específico de plataforma ni instalación por admin.",
      "Transcript en vivo corriendo durante la call (puedes ignorarlo y solo hablar).",
      "Termina la call → aparece el acta estructurada: TL;DR, puntos clave, action items. Basada en el transcript real, sin alucinaciones.",
      "Exporta como texto plano, markdown, o pega directo en la herramienta que uses. Sin lock-in de CRM.",
    ],
  },
  proof: {
    heading: "Por qué elegir Minuta en vez de Zoom AI Companion o bots de notetaker",
    bullets: [
      "Zoom AI Companion solo funciona en Zoom. Minuta funciona en Zoom, Teams, Meet, FaceTime, apps nativas, calls de navegador — cualquier cosa.",
      "Otter, Fireflies, tl;dv requieren que un bot entre en tu call. Minuta corre en tu computadora y captura el audio del sistema de forma invisible. Sin el \"Otter ha entrado en la reunión\" incómodo.",
      "Guardado en tu app de escritorio. Transcript, TL;DR y action items viven en la base de datos SQLite local — revisa, exporta a Markdown o elimina cuando quieras. Especialmente útil para calls sensibles con cliente o 1-on-1.",
      "Free tier cubre 1h/mes — suficiente para probarlo en calls reales antes de comprometerte.",
    ],
  },
  cta: {
    heading: "Deja de escribir actas",
    body: "7 días con todo desbloqueado. Después, 1h/mes en Gratis. €9/mes para Pro ilimitado.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const zoomAutoMeetingNotesCopy: Record<Lang, ZoomAutoMeetingNotesCopy> = { en, pt, es };
